// eslint-disable-next-line
import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    IconButton,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { Luminary, LuminaryList } from '../../types/luminary';
import { useSelector } from 'react-redux';
import { Selectors } from '../../selectors';
import ListItem from '@mui/material/ListItem';
import { Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AssetListItem } from '../../components/AssetListItem/AssetListItem';
import { ACTION_TYPE_NEXT } from '../../constants';
import { DeviceState, DEVICE_STATUS, getLuminaryStatusState, DEVICE_TYPE, isWirelessRepeaterType, isWirelessRadioType } from '../enum/ENUM';
import { LuminaryInfo } from './LuminaryInfo';
import { LuminaryTests } from './LuminaryTests';
import { LumImage } from '../../components/LumImage/LumImage';
import CGLineLuminaryListItem from '../../components/LuminaryList/CGLineLuminaryListItem';
import { WirelessLuminaryInfo } from './WirelessLuminaryInfo';

const luminaryDefault: Luminary = {
    luminaryId: "",
    luminaryName: "",
    type: "",
    circuit: "",
    ATSD: 123,
    lmStatus: 1,
    lmStatusName: "",
    systemEventTime: "2022-07-11T04:03:58Z",
    systemEvent: "",
    lmAddress: 19,
    serialNumber: "",
    model: "",
    luminaryDescription: "",
    functionalTestDelay: "",
    testDuration: "",
    testDurationDescription: "",
    durationTestNext: "2022-07-01T04:03:58Z",
    durationTestPrevious: "2022-06-01T04:03:58Z",
    functionalTestNext: "2022-07-01T04:03:58Z",
    functionalTestPrevious: "2022-06-01T04:03:58Z",
    dtStatus: true,
    dtTestDescription: "",
    functionalTestStatus: true,
    functionalTestDescription: "",
    luminaryEPAS: '',
    luminaryFwVersion: 0,
    luminaryFwReleaseDate: "",
    luminaryDTDuration: 0,
    luminaryLastDT: '',
    luminaryPartNumber: '',
    luminaryManufacturingDate: '',
    luminaryBatteryEPAS: '',
    luminaryBatteryChangeDate: '',
    luminaryProtocolVersion: '',
    luminaryInstallationArea: '',
    luminaryMNMConfiguration: '',
    luminaryDurationTestOddEven: '',
    modelDescription: '',
    profileId: '',
    radioEPAS: '',
    radioPartNumber: '',
    radioModelName: '',
    radioModelDescription: '',
    numberOfNeighbors: 0,
    radioSignal: 1,
    radioFirmwareVersion: '',
    cGLineId: '',
    epasShortDescription: '',
    epasModelName: '',
    radioEpasProductImgUrl: ''
}

type Props = {
    luminaryId: string;
    closeClick: Function;
    deviceType?: string;
};

export const getLuminaryImageConfig = (luminary: Luminary): { imageURL?: string; useDefaultImage: boolean } => {
    const useDefaultImage = !isWirelessRepeaterType(luminary.type);
    const imageURL = isWirelessRepeaterType(luminary.type)
        ? (luminary.epasProductImgUrl || luminary.radioEpasProductImgUrl)
        : isWirelessRadioType(luminary.type)
            ? luminary.radioEpasProductImgUrl
            : luminary.epasProductImgUrl;

    return { imageURL, useDefaultImage };
};

export const DeviceLuminaryDetails: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {

    const TAB_INFO: string = 'INFO';
    const TAB_TEST: string = 'TESTS';

    const [luminaryTab, setLuminaryTab] = React.useState(TAB_INFO);

    const { t } = useLanguageLocale();
    const asset = useSelector(Selectors.Dashboard.asset);
    const [luminaryList, setLuminaryList] = useState<LuminaryList>([]);
    const [luminary, setLuminary] = useState<Luminary>(luminaryDefault);
    const [luminaryId, setLuminaryId] = useState(props.luminaryId);

    const luminaryStatus = luminary.lmStatus;
    const { imageURL: selectedLuminaryImageUrl, useDefaultImage: useDefaultLumImage } = getLuminaryImageConfig(luminary);

    const updateLuminary = (luminaryId: string) => {
        const luminaryListUpdate: LuminaryList = luminaryList.filter((luminary) => luminary.luminaryId === luminaryId)
        if (luminaryListUpdate.length > 0) {
            setLuminary(luminaryListUpdate[0])
        }
    };

    useEffect(() => {
        setLuminaryId(props.luminaryId);
        updateLuminary(props.luminaryId);
    }, [props.luminaryId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!asset.luminaryListLoading && asset.luminaryListSuccess) {
            setLuminaryList(asset.luminaryList.detail);
            //updateLuminary();
        } else if (!asset.luminaryListLoading && asset.luminaryListFail) {
            console.log('Luminary list loading failed');
            setLuminaryList([]);
        }
    }, [asset.luminaryListLoading, asset.luminaryListSuccess, asset.luminaryListFail]); // eslint-disable-line react-hooks/exhaustive-deps

    const deviceState: DeviceState = getLuminaryStatusState(luminaryStatus);

    const handleClose = (): void => {
        props.closeClick({ luminaryId: luminaryId });
    };

    const handleErrorNext = (): void => {
    };

    const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        luminaryTab: string,
    ) => {
        if (luminaryTab !== null) setLuminaryTab(luminaryTab);
    };

    return (
        <>
            <Card style={{ flex: '1 1 0px', overflow: 'auto' }} data-testid="device-luminary-details-container">

                <ListItem style={{
                    width: '100%',
                    marginTop: 10,
                }}
                    secondaryAction={
                        <IconButton id="device-luminary-close-icon" data-testid="device-luminary-close-icon" style={{ top: -17, right: -5, marginLeft: "auto", justifyContent: 'start' }} onClick={(): void => handleClose()}>
                            <Close />
                        </IconButton>
                    }>

                    <div>
                        <Typography variant="body2" style={{ marginLeft: 5, fontSize: 18, fontWeight: 600 }} id="luminary-name" data-testid="luminary-name" >{luminary.luminaryName}</Typography>
                        <div style={{ marginTop: 10 }}>
                            {
                                [DEVICE_TYPE.DEVICE_TYPE_CGLINE, DEVICE_TYPE.DEVICE_TYPE_WIRELESS].includes(props.deviceType as DEVICE_TYPE) &&
                                <CGLineLuminaryListItem bitStatus={luminary.BitStatus ?? []} index={luminary.luminaryId} />
                            }
                            {
                                !([DEVICE_TYPE.DEVICE_TYPE_CGLINE, DEVICE_TYPE.DEVICE_TYPE_WIRELESS].includes(props.deviceType as DEVICE_TYPE)) &&
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'left',
                                    }}
                                >
                                    <div style={{ minWidth: 25 }}>
                                        <img
                                            src={deviceState.statusImage}
                                            alt="logo"
                                            style={{ display: 'flex', alignItems: 'right' }}
                                            id="device-status-icon"
                                            data-testid="device-status-icon"
                                        /></div>
                                    <Typography
                                        id="device-status-button"
                                        data-testid="device-status-button"
                                        style={{ textTransform: 'none', marginLeft: 5 }} variant="body2">
                                        {t(deviceState.statusTitle)}
                                    </Typography>
                                </div>
                            }
                        </div>
                    </div>

                </ListItem>

                {luminary.lmStatus === DEVICE_STATUS.STATUS_ERROR && <div style={{ borderRadius: 5, backgroundColor: '#F9E8E8', marginLeft: 10, marginRight: 10 }}>

                    <AssetListItem
                        key={'luminary-info-system-event'}
                        subtitle={luminary.systemEvent ? luminary.systemEvent : '--'}
                        title={(luminary.systemEventTime)}
                        imageType={ACTION_TYPE_NEXT}
                        OnItemSelected={(): void =>
                            handleErrorNext()
                        }
                        divider={false}
                        titleBold={false}
                    ></AssetListItem>

                </div>
                }
                {
                    [DEVICE_TYPE.DEVICE_TYPE_WIRELESS].includes(props.deviceType as DEVICE_TYPE) &&
                    <>
                        <div style={{ padding: '0 15px' }}>
                            <ToggleButtonGroup
                                id="luminary-toggle"
                                color="primary"
                                value={luminaryTab}
                                exclusive
                                onChange={handleChange}
                                style={{ display: 'flex' }}>
                                <ToggleButton id="luminary-toggle-info" style={{ flex: 1, height: '5vh', textTransform: 'none' }} value={TAB_INFO}>{t('DEVICE_DETAILS.LUMINARY_DETAIL_INFO')}</ToggleButton>
                                <ToggleButton id="luminary-toggle-test" style={{ flex: 1, height: '5vh', textTransform: 'none' }} value={TAB_TEST}>{t('DEVICE_DETAILS.LUMINARY_DETAIL_TEST')}</ToggleButton>
                            </ToggleButtonGroup>
                        </div>


                        {luminaryTab === TAB_INFO &&
                            <>
                                <div style={{ textAlign: 'center', padding: '10px 0px' }}>
                                    <LumImage dataTestId='luminary-epas-product-img' imageURL={selectedLuminaryImageUrl} useDefaultImage={useDefaultLumImage} />
                                </div>
                                <WirelessLuminaryInfo luminary={luminary} deviceType={props.deviceType} />
                            </>
                        }

                        {luminaryTab === TAB_TEST &&
                            <LuminaryTests
                                luminary={luminary} deviceType={props.deviceType} />
                        }
                    </>
                }

                {
                    [DEVICE_TYPE.DEVICE_TYPE_AT, DEVICE_TYPE.DEVICE_TYPE_CGLINE].includes(props.deviceType as DEVICE_TYPE) &&
                    <>
                        <div style={{ padding: '0 15px' }}>
                            <ToggleButtonGroup
                                id="luminary-toggle"
                                color="primary"
                                value={luminaryTab}
                                exclusive
                                onChange={handleChange}
                                style={{ display: 'flex' }}>
                                <ToggleButton id="luminary-toggle-info" style={{ flex: 1, height: '5vh', textTransform: 'none' }} value={TAB_INFO}>{t('DEVICE_DETAILS.LUMINARY_DETAIL_INFO')}</ToggleButton>
                                <ToggleButton id="luminary-toggle-test" style={{ flex: 1, height: '5vh', textTransform: 'none' }} value={TAB_TEST}>{t('DEVICE_DETAILS.LUMINARY_DETAIL_TEST')}</ToggleButton>
                            </ToggleButtonGroup>
                        </div>


                        {luminaryTab === TAB_INFO &&
                            <>
                                <div style={{ textAlign: 'center', padding: '10px 0px' }}>
                                    <LumImage dataTestId='luminary-epas-product-img' imageURL={selectedLuminaryImageUrl} useDefaultImage={useDefaultLumImage} />
                                </div>
                                <LuminaryInfo luminary={luminary} deviceType={props.deviceType} />
                            </>
                        }

                        {luminaryTab === TAB_TEST &&
                            <LuminaryTests
                                luminary={luminary} deviceType={props.deviceType} />
                        }
                    </>
                }
                {
                    !([DEVICE_TYPE.DEVICE_TYPE_WIRELESS, DEVICE_TYPE.DEVICE_TYPE_AT, DEVICE_TYPE.DEVICE_TYPE_CGLINE].includes(props.deviceType as DEVICE_TYPE)) &&
                    <LuminaryInfo luminary={luminary} deviceType={props.deviceType} />
                }
            </Card >
        </>
    );
};
